"use client";

import { useRequestUsersMeSettings, useSession } from "@packages/sdk";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useRequestNotificationSubscribe } from "../hooks/useRequestNotificationSubscribe";
import { urlBase64ToUint8Array } from "../utils/urlBase64ToUint8Array";

type PushSubscriptionState =
  | "idle"
  | "loading"
  | "subscribed"
  | "unsubscribed"
  | "error";

type PushSubscriptionContextValue = {
  error: Error | null;
  isSubscribed: boolean;
  state: PushSubscriptionState;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
};

const PushSubscriptionContext =
  createContext<PushSubscriptionContextValue | null>(null);

// Mount once near the root of the app so every consumer (the notifications
// settings toggle, the menu bar bell icon, etc.) shares a single subscription
// state instead of each running its own copy of this logic.
//
// `enabled` is driven by the caller's own feature-flag check (kept out of
// this package so it stays flag-agnostic). It's always mounted regardless —
// callers behind a disabled flag still call usePushSubscription() safely,
// they just get inert values back — because React hooks can't be called
// conditionally, so consumers can't skip calling the hook based on a flag
// that resolves asynchronously.
export const PushSubscriptionProvider = ({
  children,
  enabled = true,
}: {
  children: ReactNode;
  enabled?: boolean;
}) => {
  const [state, setState] = useState<PushSubscriptionState>("idle");
  const [error, setError] = useState<Error | null>(null);
  const { query: settingsQuery, mutationPut } = useRequestUsersMeSettings();
  const { subscribe: mutationSubscribe, unsubscribe: mutationUnsubscribe } =
    useRequestNotificationSubscribe();
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  // Passively check whether this browser already has an active subscription
  // (e.g. from a previous session) without registering a new service worker
  // or prompting. This reflects this browser's real state — it does not read
  // or write the account-level opt-in setting.
  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator))
      return;

    navigator.serviceWorker
      .getRegistration()
      .then((registration) => {
        registrationRef.current = registration ?? null;
        if (!registration) return null;
        return registration.pushManager.getSubscription();
      })
      .then((existingSubscription) => {
        if (existingSubscription) setState("subscribed");
      })
      .catch(() => {});
  }, [enabled]);

  const writeOptIn = useCallback(
    (value: "true" | "false") =>
      mutationPut.mutateAsync({
        settings: [{ key: "web_notification_opt_in", scope: "global", value }],
      }),
    [mutationPut],
  );

  const subscribe = useCallback(async () => {
    if (!enabled) return;

    const vapidKey = process.env.DOPPLER_PUBLIC_VAPID_PUBLIC_KEY;
    if (!vapidKey) {
      setError(new Error("VAPID public key is not configured"));
      setState("error");
      return;
    }
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      setError(new Error("Service workers are not supported"));
      setState("error");
      return;
    }

    setState("loading");
    setError(null);

    let createdSubscription: PushSubscription | null = null;
    let backendRegistered = false;

    try {
      const registration = await navigator.serviceWorker.register("/sw.js");
      registrationRef.current = registration;
      await navigator.serviceWorker.ready;

      const existingSubscription =
        await registration.pushManager.getSubscription();
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      if (!existingSubscription) createdSubscription = subscription;

      const { endpoint, expirationTime, keys } = subscription.toJSON();
      if (!endpoint || !keys?.p256dh || !keys?.auth) {
        throw new Error("Push subscription is missing required fields");
      }

      await mutationSubscribe.mutateAsync({
        body: {
          endpoint,
          expirationTime: expirationTime ?? null,
          keys: { p256dh: keys.p256dh, auth: keys.auth },
        },
      });
      backendRegistered = true;
      await writeOptIn("true");
      setState("subscribed");
    } catch (err) {
      // Undo whatever half-succeeded so the toggle, the browser and the
      // backend all agree that notifications are off.
      if (backendRegistered) {
        await mutationUnsubscribe.mutateAsync({}).catch(() => {});
      }
      await createdSubscription?.unsubscribe().catch(() => {});
      setError(err instanceof Error ? err : new Error(String(err)));
      setState("error");
    }
  }, [enabled, mutationSubscribe, mutationUnsubscribe, writeOptIn]);

  const unsubscribe = useCallback(async () => {
    if (!enabled) return;

    setState("loading");
    setError(null);

    try {
      const registration =
        registrationRef.current ??
        (await navigator.serviceWorker.getRegistration()) ??
        null;
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
      }

      await mutationUnsubscribe.mutateAsync({});
      await writeOptIn("false");
      setState("unsubscribed");
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setState("error");
    }
  }, [enabled, mutationUnsubscribe, writeOptIn]);

  const { status: sessionStatus, jwtPayload } = useSession();
  const accountId =
    jwtPayload && !jwtPayload.isAnonymous ? jwtPayload.id : null;
  const latest = useRef({ settingsQuery, subscribe });
  const reconciledAccountRef = useRef<number | null>(null);

  useEffect(() => {
    latest.current = { settingsQuery, subscribe };
  });

  // Logging out clears this device's subscription on the backend, but the
  // browser-level PushSubscription survives. Re-register with the backend once
  // per authenticated account so notifications keep working without the user
  // revisiting the settings toggle. Refetch rather than trust a render-time
  // snapshot, since the settings query only starts fetching once a session
  // token exists.
  useEffect(() => {
    if (sessionStatus !== "authenticated" || accountId === null) {
      reconciledAccountRef.current = null;
      return;
    }
    if (!enabled) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    if (reconciledAccountRef.current === accountId) return;

    reconciledAccountRef.current = accountId;
    let cancelled = false;
    let finished = false;

    const reconcile = async () => {
      try {
        const result = await latest.current.settingsQuery.refetch();
        if (cancelled) return;
        if (result.data?.global.web_notification_opt_in !== "true") return;
        await latest.current.subscribe();
      } finally {
        finished = true;
      }
    };
    void reconcile();

    return () => {
      cancelled = true;
      // Let an interrupted reconcile (e.g. flag flipped off) run again later
      if (!finished) reconciledAccountRef.current = null;
    };
  }, [enabled, sessionStatus, accountId]);

  return (
    <PushSubscriptionContext.Provider
      value={{
        error,
        isSubscribed: state === "subscribed",
        state,
        subscribe,
        unsubscribe,
      }}
    >
      {children}
    </PushSubscriptionContext.Provider>
  );
};

export const usePushSubscription = () => {
  const context = useContext(PushSubscriptionContext);
  if (!context) {
    throw new Error(
      "usePushSubscription must be used within a PushSubscriptionProvider",
    );
  }
  return context;
};
